import AddIcon from '@mui/icons-material/Add'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useProducts } from 'api/aws/useProducts'
import dayjs, { Dayjs } from 'dayjs'
import React, { useMemo, useState } from 'react'

interface SelectedProduct {
  productName: string
  price: number
  margin: number
  qty: number
  category?: string
}

interface MajorVersionConfigFormProps {
  title: string
  dealPK: string
  dealSK: string
  dealName: string
  currentVersion: string
  currentProducts?: Array<{ productName: string; price: number; margin: number; qty: number }>
  onSubmit: (message: string) => void
  isInteractive: boolean
  deals: any[]
}

const MajorVersionConfigForm: React.FC<MajorVersionConfigFormProps> = ({
  title,
  dealPK,
  dealSK,
  dealName,
  currentVersion,
  currentProducts,
  onSubmit,
  isInteractive,
  deals,
}) => {
  const theme = useTheme()
  const { data: catalogProducts = [] } = useProducts()
  const currentDeal = deals.find(d => d?.PK === dealPK && d?.SK === dealSK)

  const [startDate, setStartDate] = useState<Dayjs | null>(
    currentDeal?.endDate ? dayjs(currentDeal.endDate).add(1, 'day') : null
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(null)
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    currentProducts?.map(p => ({ ...p })) ?? []
  )
  const [submitted, setSubmitted] = useState(false)

  const activeCatalog = useMemo(
    () =>
      catalogProducts
        .filter(p => !p.isArchived)
        .sort((a, b) => (a.title || a.productName || '').localeCompare(b.title || b.productName || '')),
    [catalogProducts]
  )

  const handleAddFromCatalog = (_: any, product: any) => {
    if (!product) return
    setSelectedProducts(prev => [
      ...prev,
      {
        productName: product.title || product.productName || '',
        price: Number(product.price) || 0,
        margin: Number(product.margin) || 0,
        qty: 1,
        category: product.category_name || product.category,
      },
    ])
  }

  const handleUpdateProduct = (index: number, field: keyof SelectedProduct, value: any) => {
    setSelectedProducts(prev => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)))
  }

  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    if (!startDate || !endDate || selectedProducts.length === 0) return

    const productLines = selectedProducts
      .map(p => `  - ${p.productName}: $${p.price}/mo, ${p.margin}% margin, qty ${p.qty}`)
      .join('\n')

    const message = [
      `Create major version for "${dealName}" (PK: ${dealPK}, SK: ${dealSK}, current version: ${currentVersion}):`,
      `- Start date: ${startDate.format('YYYY-MM-DD')}`,
      `- End date: ${endDate.format('YYYY-MM-DD')}`,
      `- Products:`,
      productLines,
      'Please proceed with creating this major version.',
    ].join('\n')

    setSubmitted(true)
    onSubmit(message)
  }

  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 420,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted
            ? `Major version config submitted — ${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''}`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 1.5,
          py: 1.5,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 420,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {title}
        </Typography>

        {/* Date Range */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem', bgcolor: 'background.default' },
                },
              },
            }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            minDate={startDate || undefined}
            onChange={setEndDate}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem', bgcolor: 'background.default' },
                },
              },
            }}
          />
        </Box>

        {/* Product Search / Add */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Products ({selectedProducts.length})
          </Typography>
          <Autocomplete
            options={activeCatalog}
            getOptionLabel={opt => opt.title || opt.productName || ''}
            groupBy={opt => opt.category_name || opt.category || 'Uncategorized'}
            onChange={handleAddFromCatalog}
            value={null}
            blurOnSelect
            clearOnBlur
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                placeholder="Search & add products..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.8rem', bgcolor: 'background.default' },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }} noWrap>
                  {option.title || option.productName}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: 'text.success' }}>
                    ${Number(option.price || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.margin}%
                  </Typography>
                </Stack>
              </Box>
            )}
            sx={{ mb: 1 }}
          />

          {/* Selected Products List */}
          <Stack gap={0.5} sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {selectedProducts.map((product, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography noWrap variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, flex: 1, minWidth: 0 }}>
                  {product.productName}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={product.price}
                  onChange={e => handleUpdateProduct(index, 'price', Number(e.target.value))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
                    '& .MuiInputAdornment-root': { mr: 0 },
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={product.qty}
                  onChange={e => handleUpdateProduct(index, 'qty', Math.max(1, Number(e.target.value)))}
                  sx={{
                    width: 50,
                    '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
                  }}
                />
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleRemoveProduct(index)} sx={{ p: 0.25 }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            {selectedProducts.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', py: 1 }}>
                No products added yet
              </Typography>
            )}
          </Stack>
        </Box>

        <Button
          size="small"
          variant="outlined"
          onClick={handleSubmit}
          disabled={!startDate || !endDate || selectedProducts.length === 0}
          sx={{
            alignSelf: 'flex-end',
            fontWeight: 500,
            fontSize: '0.75rem',
            borderColor: 'text.ai_agent_text',
            color: 'text.ai_agent_text',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              borderColor: 'text.highlight',
              color: 'text.primary',
            },
          }}
        >
          Configure Major Version
        </Button>
      </Box>
    </LocalizationProvider>
  )
}

export default MajorVersionConfigForm
